import React, { FunctionComponent } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import ReCAPTCHA from "react-google-recaptcha";
import { Field, FormikErrors, FormikValues } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { goBack } from "../../../../actions/diversityFormActions";
import { AppState } from "../../../../store";
import RadioButton from "../../../shared/publicForms/RadioButton";
import Yes from "./Yes";
import EditButton from "./EditButton";
import SubmitButton from "./SubmitButton";
import {
  StepWrapper,
  Title,
  RadioButtonGroupWrapper,
  RadioButtonGroup,
  BackButton,
  ButtonsWrapper,
  ReCaptchaContainer,
} from "../../styles/diversity-form-styles";

export interface ICaringResponsibilitiesProps {
  values: {
    caringResponsibilities?: null | string;
    caringResponsibilitiesType?: null | string;
  };
  errors: FormikErrors<FormikValues>;
  setFieldValue: (
    field: string,
    value: unknown,
    shouldValidate: boolean
  ) => void;
  formLoading: boolean;
  goBackAction: typeof goBack;
}

export const CaringResponsibilities: FunctionComponent<
  ICaringResponsibilitiesProps
> = ({ values, errors, goBackAction, setFieldValue, formLoading }) => {
  const { caringResponsibilities, caringResponsibilitiesType } = values;
  const recaptchaKey = process.env.RECAPTCHA_SITE_KEY || "";
  const radioButtonOptions = ["Yes", "No"];

  return (
    <StepWrapper data-test-id="ethnicity-step" formStep>
      <Title data-test-id="title">Do you have caring responsibilities?</Title>
      {caringResponsibilities === "Yes" && (
        <EditButton
          formLoading={formLoading}
          setFieldValue={setFieldValue}
          caringResponsibilities={caringResponsibilities}
        />
      )}
      {(!caringResponsibilities || caringResponsibilities === "No") && (
        <RadioButtonGroupWrapper
          role="group"
          aria-labelledby="caring-responsibilities-radio-group"
        >
          <RadioButtonGroup width={90}>
            {radioButtonOptions.map((option) => (
              <Field
                key={option}
                name="caringResponsibilities"
                type="radio"
                disabled={formLoading}
                aria-label={option}
                label={option}
                value={option}
                onClick={() =>
                  setFieldValue("caringResponsibilitiesType", null, true)
                }
                as={RadioButton}
              />
            ))}
          </RadioButtonGroup>
        </RadioButtonGroupWrapper>
      )}
      {caringResponsibilities === "Yes" && (
        <Yes
          values={values}
          setFieldValue={setFieldValue}
          errors={errors}
          formLoading={formLoading}
        />
      )}
      <ReCaptchaContainer disabled={formLoading}>
        <ReCAPTCHA
          sitekey={recaptchaKey}
          onChange={(response: string) => {
            setFieldValue("recaptcha", response, true);
          }}
        />
      </ReCaptchaContainer>
      <ButtonsWrapper>
        <BackButton type="button" onClick={goBackAction} disabled={formLoading}>
          <FontAwesomeIcon icon={faChevronLeft} />
          back
        </BackButton>
        <SubmitButton
          caringResponsibilities={caringResponsibilities}
          caringResponsibilitiesType={caringResponsibilitiesType}
          errors={errors}
        />
      </ButtonsWrapper>
    </StepWrapper>
  );
};

CaringResponsibilities.propTypes = {
  values: PropTypes.shape({
    caringResponsibilities: PropTypes.string,
    caringResponsibilitiesType: PropTypes.string,
  }).isRequired,
  errors: PropTypes.shape({
    caringResponsibilities: PropTypes.string,
    caringResponsibilitiesType: PropTypes.string,
    recaptcha: PropTypes.string,
  }).isRequired,
  goBackAction: PropTypes.func.isRequired,
  formLoading: PropTypes.bool.isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

const mapStateToProps = (state: AppState) => ({
  formLoading: state.diversityForm.formLoading,
});

export default connect(mapStateToProps, { goBackAction: goBack })(
  CaringResponsibilities
);
